import { CloudOff, LogOut, Map as MapIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router";
import { LanguageSelect } from "../components/LanguageSelect.tsx";
import { useLocale } from "../i18n/index.ts";
import { useOnline } from "../pwa/online.ts";
import { AuditPage } from "./AuditPage.tsx";
import { LakesPage } from "./LakesPage.tsx";
import { loginUrl } from "../auth/next.ts";
import { SpotFormPage } from "./SpotFormPage.tsx";
import { SpotsPage } from "./SpotsPage.tsx";
import { SuggestionDetailPage } from "./SuggestionDetailPage.tsx";
import { SuggestionsPage, usePendingCount } from "./SuggestionsPage.tsx";
import { SettingsPage } from "./SettingsPage.tsx";
import { UsersPage } from "./UsersPage.tsx";
import { useSession, useSessionActions } from "./session.ts";

function RequireStaff() {
  const { t } = useTranslation();
  const locale = useLocale();
  const session = useSession();
  const location = useLocation();
  if (session.status === "loading") return <p role="status">{t("common.loading")}</p>;
  if (session.status === "user" && !session.staff) {
    // a diver (self-registered account) has no business in the admin
    return (
      <div className="grid gap-3" role="alert">
        <h1 className="ds-text-title-1 m-0">{t("admin.forbidden.title")}</h1>
        <p className="ds-text-body m-0">{t("admin.forbidden.staffOnly")}</p>
        <Link className="ds-btn ds-btn--tinted" to={`/${locale}/`}>
          {t("admin.toMap")}
        </Link>
      </div>
    );
  }
  if (session.status === "anonymous") {
    return <Navigate to={loginUrl(locale, location.pathname + location.search)} replace />;
  }
  return <Outlet />;
}

function RequireAdmin() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { user } = useSession();
  if (user?.role !== "admin") {
    return (
      <div className="grid gap-3" role="alert">
        <h1 className="ds-text-title-1 m-0">{t("admin.forbidden.title")}</h1>
        <p className="ds-text-body m-0">{t("admin.forbidden.body")}</p>
        <Link className="ds-btn ds-btn--tinted" to={`/${locale}/admin/spots`}>
          {t("admin.forbidden.back")}
        </Link>
      </div>
    );
  }
  return <Outlet />;
}

function AdminHeader() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { user, staff } = useSession();
  const actions = useSessionActions();
  const navigate = useNavigate();
  const link = ({ isActive }: { isActive: boolean }) =>
    `adm-navlink${isActive ? " adm-navlink--active" : ""}`;
  const pending = usePendingCount(staff);
  // on phones the nav is one scrollable row: keep the current page's item in view
  const navRef = useRef<HTMLElement>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const el = navRef.current;
    const active = el?.querySelector<HTMLElement>(".adm-navlink--active");
    if (!el || !active) return;
    el.scrollTo?.({ left: active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2 });
  }, [pathname, staff, user?.role]);
  return (
    <header className="adm-header adm-header--scroll">
      <Link to={`/${locale}/admin/spots`} className="adm-brand ds-text-headline">
        {t("admin.brand")}
      </Link>
      {staff ? (
        <nav ref={navRef} aria-label={t("admin.nav")} className="adm-nav">
          <NavLink to={`/${locale}/admin/spots`} className={link}>
            {t("admin.nav_spots")}
          </NavLink>
          <NavLink to={`/${locale}/admin/vorschlaege`} className={link}>
            {t("admin.nav_suggestions")}
            {pending ? (
              <span className="adm-count" aria-label={t("review.pendingCount", { count: pending })}>
                {pending > 99 ? "99+" : pending}
              </span>
            ) : null}
          </NavLink>
          {user?.role === "admin" ? (
            <>
              <NavLink to={`/${locale}/admin/lakes`} className={link}>
                {t("admin.nav_lakes")}
              </NavLink>
              <NavLink to={`/${locale}/admin/users`} className={link}>
                {t("admin.nav_users")}
              </NavLink>
              <NavLink to={`/${locale}/admin/settings`} className={link}>
                {t("admin.nav_settings")}
              </NavLink>
              <NavLink to={`/${locale}/admin/protokoll`} className={link}>
                {t("admin.nav_audit")}
              </NavLink>
            </>
          ) : null}
          <NavLink to={`/${locale}/konto`} className={(s) => `${link(s)} adm-only-sm`}>
            {t("diver.nav_account")}
          </NavLink>
        </nav>
      ) : (
        <span />
      )}
      <div className="adm-header__right">
        <LanguageSelect plain />
        <Link className="ds-btn ds-btn--tinted" to={`/${locale}/`} aria-label={t("admin.toMap")}>
          <MapIcon aria-hidden="true" strokeWidth={1.75} />
          <span className="adm-hide-sm">{t("admin.toMap")}</span>
        </Link>
        {user ? (
          <>
            <Link
              className="ds-text-footnote text-label-secondary adm-hide-sm adm-accountlink"
              to={`/${locale}/konto`}
              aria-label={t("admin.accountLink", {
                name: user.name,
                role: t(`admin.role.${user.role}`),
              })}
            >
              {user.name} · {t(`admin.role.${user.role}`)}
            </Link>
            <button
              type="button"
              className="ds-btn ds-btn--plain"
              aria-label={t("admin.signOut")}
              onClick={async () => {
                await actions.signOutAndClear();
                void navigate(`/${locale}/`);
              }}
            >
              <LogOut aria-hidden="true" strokeWidth={1.75} />
              <span className="adm-hide-sm">{t("admin.signOut")}</span>
            </button>
          </>
        ) : null}
      </div>
    </header>
  );
}

/** Everything under `/:lang/admin`: login, then spots (moderator) and lakes/users (admin). */
export function AdminApp() {
  const { t } = useTranslation();
  const locale = useLocale();
  const online = useOnline();
  useEffect(() => {
    document.title = `${t("admin.brand")} · ${t("app.name")}`;
  }, [t]);
  return (
    <div className="adm-root">
      <AdminHeader />
      <main className="adm-main">
        {online ? null : (
          <p className="adm-warning" role="alert">
            <CloudOff aria-hidden="true" size={20} strokeWidth={1.75} />
            {t("offline.adminNeedsConnection")}
          </p>
        )}
        <Routes>
          {/* the one login is /:lang/login; the old address keeps working for bookmarks */}
          <Route
            path="login"
            element={<Navigate to={loginUrl(locale, `/${locale}/admin`)} replace />}
          />
          <Route element={<RequireStaff />}>
            <Route index element={<Navigate to="spots" replace />} />
            <Route path="spots" element={<SpotsPage />} />
            <Route path="spots/new" element={<SpotFormPage />} />
            <Route path="spots/:id" element={<SpotFormPage />} />
            <Route path="vorschlaege" element={<SuggestionsPage />} />
            <Route path="vorschlaege/:id" element={<SuggestionDetailPage />} />
            <Route element={<RequireAdmin />}>
              <Route path="lakes" element={<LakesPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="protokoll" element={<AuditPage />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="spots" replace />} />
        </Routes>
      </main>
    </div>
  );
}
